// features/cart/components/cart-content.tsx
"use client"

import * as React from "react"
import Image from "next/image"
import Link from "next/link"
import { useQuery } from "@tanstack/react-query"
import { ChevronRight, ShoppingBag } from "lucide-react"

import { getRestaurantDetail } from "@/lib/client/api/restaurant"
import { Category } from "@/lib/types"
import { RestaurantCart } from "@/lib/types/cart"
import { formatCurrency } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { ScrollArea } from "@/components/ui/scroll-area"
import { SheetTitle } from "@/components/ui/sheet"
import { Icons } from "@/components/icons"
import { NumberIncrementer } from "@/components/number-incrementer"

import { useCartDetail } from "../hooks/use-cart-detail"
import { CartContentSkeleton } from "./cart-content-skeleton"

// Helper component for empty cart state
function EmptyCart() {
  return (
    <div className="flex flex-col items-center justify-center h-full p-8 text-center">
      <Icons.emptyCart
        size={48}
        className="mx-auto text-gray-300 mb-4"
        aria-hidden="true"
      />
      <p className="text-gray-500 text-sm">Giỏ hàng hiện đang trống</p>
    </div>
  )
}

// Restaurant card component for the all carts view
function RestaurantCartCard({ cart }: { cart: RestaurantCart }) {
  const itemCount = cart.restaurant_items.reduce(
    (sum, item) => sum + item.quantity,
    0
  )

  return (
    <Link
      href={`/resto/${cart.restaurant_id}`}
      className="flex p-4 cursor-pointer hover:bg-gray-50"
    >
      <div className="relative size-16 shrink-0 rounded-md overflow-hidden mr-3">
        {cart.restaurant_image ? (
          <Image
            src={cart.restaurant_image}
            alt={cart.restaurant_name}
            fill
            className="object-cover"
          />
        ) : (
          <div className="w-full h-full bg-gray-100 flex items-center justify-center">
            <ShoppingBag className="size-6 text-gray-400" />
          </div>
        )}
      </div>

      <div className="flex-1">
        <h3 className="font-medium text-be-text-default line-clamp-2 text-sm">
          {cart.restaurant_name}
        </h3>
        <p className="text-xs text-be-text-secondary flex items-center">
          <span className="font-bold">{formatCurrency(cart.sub_total)}</span>
          <span className="ml-1 text-gray-600"> ({itemCount} phần)</span>
        </p>
      </div>

      <ChevronRight className="size-5 text-gray-400" />
    </Link>
  )
}

interface CartContentProps {
  restaurantId?: number
}

export function CartContent({ restaurantId }: CartContentProps) {
  // Use React Query to fetch cart data
  const { data: cartResponse, isLoading, error } = useCartDetail()

  const { data: dishMapData } = useQuery({
    queryKey: ["restaurant", "dishes", restaurantId],
    queryFn: async () => {
      const res = await getRestaurantDetail(restaurantId!)
      const dishes = res.data.categories.map(
        (category: Category) => category.items
      )

      const allDishes = dishes.flat()

      const dishMap = allDishes.reduce(
        (
          acc: Record<number, Category["items"][number]>,
          dish: Category["items"][number]
        ) => {
          acc[dish.restaurant_item_id] = dish
          return acc
        },
        {} as Record<number, Category["items"][number]>
      )

      return dishMap
    },
    enabled: !!restaurantId,
    placeholderData: {},
    staleTime: 10 * 60 * 1000,
  })

  // --- Rendering Logic ---
  if (isLoading) {
    return <CartContentSkeleton />
  }

  if (
    error ||
    !cartResponse ||
    !cartResponse.carts ||
    cartResponse.carts.length === 0
  ) {
    return <EmptyCart />
  }

  // If restaurantId is provided, show items from that specific restaurant
  if (restaurantId) {
    const selectedCart = cartResponse.carts.find(
      (cart) => cart.restaurant_id === restaurantId
    )

    if (!selectedCart) {
      return <EmptyCart />
    }

    const dishMap = dishMapData || {}

    console.log({ selectedCart })

    return (
      <div className="flex flex-col h-full">
        <div className="p-4 border-b">
          <SheetTitle className="text-lg font-semibold">Giỏ hàng</SheetTitle>
        </div>

        <ScrollArea className="flex-1">
          <div className="p-4 space-y-4">
            {selectedCart.restaurant_items.map((item) => (
              <div
                key={item.item_id}
                className="flex flex-col border-b pb-4 gap-1"
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex-1 space-y-1">
                    <h3 className="font-medium text-sm">
                      {dishMap[item.item_id]?.item_name || "Món ăn"}
                    </h3>
                    {item.customise_object && (
                      <p className="text-xs text-gray-500">
                        {item.customise_object}
                      </p>
                    )}

                    <p className="text-xs text-gray-500 pt-1">
                      <span className="flex items-center gap-1">
                        <Icons.note className="size-4" />{" "}
                        {item.note || "Ghi chú cho quán"}
                      </span>
                    </p>
                  </div>
                  <p className="font-semibold text-sm">
                    {formatCurrency(item.price)}
                  </p>
                </div>

                <div className="flex items-center justify-between">
                  <NumberIncrementer
                    value={item.quantity}
                    disabled={true}
                    className="h-8"
                  />
                </div>
              </div>
            ))}
          </div>
        </ScrollArea>

        <div className="p-4 space-y-3 border-t border-gray-200/50">
          <div className="flex justify-between text-sm">
            <span>Tạm tính</span>
            <span>{formatCurrency(selectedCart.sub_total)}</span>
          </div>

          <div className="flex justify-between text-base font-semibold">
            <span>Tổng cộng</span>
            <span>{formatCurrency(selectedCart.sub_total)}</span>
          </div>

          <Button variant="be" className="w-full h-11 rounded-lg">
            Xem đơn hàng • {formatCurrency(selectedCart.sub_total)}
          </Button>
        </div>
      </div>
    )
  }

  // If no restaurantId is provided, show all restaurant carts
  return (
    <div className="flex flex-col h-full">
      <div className="p-4 border-b-6 border-gray-200/50">
        <h2 className="text-lg font-semibold">Giỏ hàng của tôi</h2>
        <p className="text-xs text-gray-500">
          {cartResponse.carts.length} nhà hàng
        </p>
      </div>

      <ScrollArea className="flex-1">
        <div className="divide-y divide-gray-200/40">
          {cartResponse.carts.map((cart) => (
            <RestaurantCartCard key={cart.cart_id} cart={cart} />
          ))}
        </div>
      </ScrollArea>
    </div>
  )
}
